<template>
  <dp-container v-if="visible" col-class="pt-3">
    <div v-testid="'plus-savings-banner'" class="plus-savings-banner alert alert-success" :data-variant="variant">
      <div class="plus-savings-banner__copy">
        <strong class="plus-savings-banner__headline">{{ headline }}</strong>

        <i18n
          v-for="pet in memberPetNames"
          :key="pet"
          path="plusSavingsBanner.member.includedFor"
          tag="span"
          class="plus-savings-banner__included"
        >
          <template #petName>{{ pet }}</template>
          <template #tags
            ><b-link v-bind="benefitLinks.tags" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.tags')
            }}</b-link></template
          >
          <template #tracker
            ><b-link v-bind="benefitLinks.tracker" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.tracker')
            }}</b-link></template
          >
          <template #walletCards
            ><b-link v-bind="benefitLinks.walletCards" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.walletCards')
            }}</b-link></template
          >
          <template #collar
            ><b-link v-bind="benefitLinks.collars" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.collar')
            }}</b-link></template
          >
        </i18n>

        <i18n v-if="!isMemberVariant" path="plusSavingsBanner.upsell.includes" tag="span" class="plus-savings-banner__included">
          <template #tags
            ><b-link v-bind="benefitLinks.tags" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.tags')
            }}</b-link></template
          >
          <template #tracker
            ><b-link v-bind="benefitLinks.tracker" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.tracker')
            }}</b-link></template
          >
          <template #walletCards
            ><b-link v-bind="benefitLinks.walletCards" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.walletCards')
            }}</b-link></template
          >
          <template #collar
            ><b-link v-bind="benefitLinks.collars" class="plus-savings-banner__benefit">{{
              $t('plusSavingsBanner.benefits.collar')
            }}</b-link></template
          >
        </i18n>

        <span class="plus-savings-banner__footnote">
          {{ $t('plusSavingsBanner.autoApplied') }}
          <b-link
            v-if="isMemberVariant"
            v-testid="'plus-savings-banner-shop-now'"
            :href="storeUrl"
            class="plus-savings-banner__benefit"
            @click="trackClick('shop_now')"
            >{{ $t('plusSavingsBanner.member.shopNow') }}</b-link
          >
          <b-link
            v-else
            v-testid="'plus-savings-banner-select-plan'"
            :href="membershipUrl"
            class="plus-savings-banner__benefit"
            @click="trackClick('select_plan')"
            >{{ $t('plusSavingsBanner.upsell.selectPlan') }}</b-link
          >
        </span>
      </div>

      <div class="plus-savings-banner__actions">
        <template v-if="showsBasketAffordance">
          <span class="plus-savings-banner__item-count">{{ itemCountLabel }}</span>
          <b-link
            v-testid="'plus-savings-banner-basket-link'"
            :href="basketUrl"
            class="plus-savings-banner__basket-link"
            @click="trackClick('view_basket')"
            >{{ $t('plusSavingsBanner.member.viewBasket') }}</b-link
          >
        </template>

        <b-link
          v-else
          v-testid="'plus-savings-banner-membership-cta'"
          :href="membershipUrl"
          class="plus-savings-banner__cta"
          @click="trackClick('see_plans')"
          >{{ $t('plusSavingsBanner.upsell.cta') }}</b-link
        >

        <button
          v-testid="'plus-savings-banner-dismiss'"
          type="button"
          class="plus-savings-banner__close"
          :aria-label="$t('plusSavingsBanner.dismiss')"
          @click="dismiss"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  </dp-container>
</template>

<script>
import { DpContainer } from '@docupet/component-library';
import { mapGetters } from 'vuex';
import { pushAnalyticsEvent } from '@/utils/analytics';

const BANNER_SOURCE = 'shop_hub_banner';
const HEADLINE_KEYS = { 1: 'one', 2: 'two', 3: 'three' };

export default {
  name: 'PlusSavingsBanner',
  components: {
    DpContainer,
  },
  data() {
    return {
      dismissed: false,
      impressionTrackedVariant: null,
    };
  },
  computed: {
    ...mapGetters({
      automatedDiscountsEnabled: 'automatedDiscountsEnabled',
      generateFullUrl: 'generateFullUrl',
      hasPromoCode: 'order/hasPromoCode',
      membershipPetNames: 'order/membershipPetNames',
      itemsCount: 'order/itemsCount',
      isPetOwner: 'user/isPetOwner',
      petMembershipsEnabled: 'user/petMembershipsEnabled',
      plusActivePetNames: 'user/plusActivePetNames',
      hasUpsellEligiblePet: 'user/hasUpsellEligiblePet',
    }),
    visible() {
      return (
        this.automatedDiscountsEnabled &&
        this.petMembershipsEnabled &&
        !this.hasPromoCode &&
        !this.dismissed &&
        (this.isMemberVariant || this.canUpsell)
      );
    },
    memberPetNames() {
      return [...new Set([...this.plusActivePetNames, ...this.membershipPetNames])];
    },
    isMemberVariant() {
      return this.memberPetNames.length > 0;
    },
    canUpsell() {
      return this.isPetOwner ? this.hasUpsellEligiblePet : true;
    },
    variant() {
      return this.isMemberVariant ? 'member' : 'upsell';
    },
    benefitLinks() {
      return {
        tags: { to: { name: 'TagStore' } },
        tracker: { href: this.generateFullUrl('/store/smart-tracker') },
        walletCards: { to: { name: 'WalletCardStore' } },
        collars: { to: { name: 'CollarStore' } },
      };
    },
    headline() {
      if (!this.isMemberVariant) return this.$t('plusSavingsBanner.upsell.headline');

      const [first, second, third] = this.memberPetNames;
      const key = HEADLINE_KEYS[this.memberPetNames.length] || 'many';

      return this.$t(`plusSavingsBanner.member.headline.${key}`, { first, second, third });
    },
    itemCountLabel() {
      return this.$tc('plusSavingsBanner.member.itemCount', this.itemsCount, { count: this.itemsCount });
    },
    basketUrl() {
      return this.generateFullUrl('/basket');
    },
    storeUrl() {
      return this.generateFullUrl('/store');
    },
    membershipUrl() {
      return `${this.generateFullUrl('/membership/upgrade')}?source=${BANNER_SOURCE}`;
    },
    showsBasketAffordance() {
      return this.isMemberVariant && this.itemsCount > 0;
    },
    bannerState() {
      return { visible: this.visible, variant: this.variant, showsBasketAffordance: this.showsBasketAffordance };
    },
  },
  watch: {
    bannerState: {
      immediate: true,
      handler({ visible, variant, showsBasketAffordance }) {
        this.$emit('banner-state', { visible, variant, showsBasketAffordance });
        if (visible) this.trackImpression();
      },
    },
  },
  methods: {
    dismiss() {
      this.dismissed = true;
    },
    trackImpression() {
      if (!this.visible || this.impressionTrackedVariant === this.variant) return;

      this.impressionTrackedVariant = this.variant;
      pushAnalyticsEvent('plus_savings_banner_impression', {
        banner_variant: this.variant,
        banner_source: BANNER_SOURCE,
      });
    },
    trackClick(target) {
      pushAnalyticsEvent('plus_savings_banner_click', {
        banner_variant: this.variant,
        banner_source: BANNER_SOURCE,
        banner_target: target,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';

$banner-breakpoint: 767.98px;

.plus-savings-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0;
  padding: 0.75rem 0.875rem;

  &__copy {
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
  }

  &__headline {
    font-size: 0.845rem;
    font-weight: 700;
  }

  &__included,
  &__footnote {
    font-size: 0.78rem;
    line-height: 1.55;
  }

  &__footnote {
    margin-top: 0.1875rem;
  }

  &__benefit {
    font-weight: 700;
    color: inherit;
    text-decoration: underline;

    &:hover {
      color: inherit;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    align-self: flex-start;
    flex: none;
    gap: 0.625rem;
    white-space: nowrap;
  }

  &__item-count {
    font-size: 0.8125rem;
  }

  &__basket-link {
    font-size: 0.8125rem;
    color: $sky-700;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    height: 2.125rem;
    padding: 0 1.125rem;
    background-color: $dp-green;
    color: $dp-white;
    font-size: 0.78rem;
    font-weight: 700;
    border-radius: 999px;
    text-decoration: none;

    &:hover {
      background-color: darken($dp-green, 8%);
      color: $dp-white;
    }
  }

  &__close {
    padding: 0;
    border: 0;
    background: none;
    line-height: 1;
    font-size: 1.125rem;
    color: inherit;
    opacity: 0.55;
    cursor: pointer;
  }

  @media (max-width: $banner-breakpoint) {
    flex-direction: column;

    &__actions {
      align-self: stretch;
      justify-content: flex-end;
    }
  }
}
</style>
